import { Navigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, MapPin, Building2, CalendarClock, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { COMMUNITY_PAGES, COMMUNITY_HUB, HANDOVER_STATUS_LABEL, relatedLabel, communityNarrative, developerUrl, COMMUNITY_INFO_REVIEWED } from "@/data/communityPages";
import { serviceSchema, breadcrumbSchema, faqSchema } from "@/lib/seo";
import { PRICE_GUIDE, PRICING_NOTE, REPORT_PROMISE } from "@/lib/business";
import { waQuoteHref } from "@/lib/business";
import { prefillFor } from "@/lib/prefill";

export default function CommunityPage({ slug }) {
  const data = COMMUNITY_PAGES[slug];
  if (!data) return <Navigate to="/404" replace />;

  const path = `/${slug}`;
  const prefill = prefillFor(path) || { service: "Snagging inspection", community: data.communityName };
  const wa = waQuoteHref({ inspectionType: prefill.service, community: prefill.community || data.communityName, project: prefill.project || "" });

  const breadcrumbs = [
    { name: "Dubai Communities", path: COMMUNITY_HUB },
    { name: data.communityName, path },
  ];
  const jsonLd = [
    serviceSchema({ name: data.h1, description: data.metaDescription, serviceType: data.inspectionIntent, areaServed: [data.emirate], path }),
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Dubai Communities", path: COMMUNITY_HUB }, { name: data.communityName, path }]),
    faqSchema(data.faqs),
  ];

  return (
    <PageShell breadcrumbs={breadcrumbs} quoteContext={prefill}>
      {({ onQuote }) => (
        <>
          <Seo title={data.title} description={data.metaDescription} path={path} jsonLd={jsonLd} />

          {/* Hero */}
          <section className="wrap pt-6 pb-10 lg:pt-10 lg:pb-14">
            <span className="eyebrow">Independent property inspection in {data.communityName}</span>
            <h1 className="text-[clamp(1.9rem,4vw,3rem)] leading-[1.07] tracking-[-0.03em] mt-3 mb-5 max-w-[22ch]">{data.h1}</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[64ch] mb-4">{data.uniqueSalesAngle}</p>
            <p className="text-sm text-muted max-w-[64ch] mb-7">
              Licensed building inspection service, independent scope, report {REPORT_PROMISE}.
            </p>
            <div className="flex flex-wrap items-center gap-2.5 mb-7">
              <Chip icon={MapPin}>{data.locationLabel}, {data.emirate}</Chip>
              <Chip icon={Building2}>{data.developerName}</Chip>
              <Chip icon={CalendarClock}>{HANDOVER_STATUS_LABEL[data.handoverStatus]}</Chip>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button onClick={onQuote} data-testid="community-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                Get quote &amp; availability <ArrowRight size={17} />
              </button>
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="community-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> WhatsApp Fedari
              </a>
            </div>
          </section>

          {/* Handover context */}
          <section className="wrap pb-12">
            <div className="bg-soft border border-line rounded-2xl p-6 lg:p-8 max-w-[80ch]">
              <h2 className="text-xl mb-3">Handover status in {data.communityName}</h2>
              <p className="text-bodytext">{data.handoverSummary}</p>
              <p className="text-xs text-muted mt-4">Handover dates are set by the developer and can change — always confirm the current schedule with {data.developerName}.</p>
            </div>
          </section>

          {/* Scope + property types */}
          <div className="wrap grid lg:grid-cols-2 gap-x-14 gap-y-12 pb-14">
            <section>
              <h2 className="text-xl mb-4">What we check in {data.communityName}</h2>
              <ul className="space-y-2.5">
                {data.inspectionFocus.map((it, i) => (
                  <li key={i} className="flex gap-2.5 text-bodytext">
                    <CheckCircle2 size={18} className="text-blue flex-none mt-0.5" aria-hidden="true" /> {it}
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2 className="text-xl mb-4">Property types &amp; projects</h2>
              <div className="flex flex-wrap gap-2 mb-5">
                {data.propertyTypes.map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-full border border-line text-sm text-bodytext bg-soft">{p}</span>
                ))}
              </div>
              <h3 className="font-semibold text-ink mb-2 text-sm">Projects we cover</h3>
              <div className="flex flex-wrap gap-2">
                {data.projects.map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-full border border-line text-sm text-muted">{p}</span>
                ))}
              </div>
            </section>
          </div>

          {/* In-depth, evergreen community content */}
          <section className="wrap pb-12 max-w-[80ch]" data-testid="community-narrative">
            {communityNarrative(data).map((sec, i) => (
              <div key={i} className="mb-8">
                <h2 className="text-xl mb-3">{sec.h}</h2>
                {sec.paras.map((p, j) => (
                  <p key={j} className="text-bodytext leading-relaxed mb-3">{p}</p>
                ))}
              </div>
            ))}
            <div className="rounded-xl border border-line bg-soft p-5 text-sm text-muted" data-testid="community-review-note">
              <p className="mb-1">
                <span className="font-semibold text-ink">Information reviewed:</span> {COMMUNITY_INFO_REVIEWED}.
                {developerUrl(data.developerName) && (
                  <> Community and project details are published by the developer, {" "}
                    <a href={developerUrl(data.developerName)} target="_blank" rel="noopener noreferrer nofollow" className="text-blue font-medium hover:underline" data-testid="community-developer-link">{data.developerName} (official site)</a>. Always confirm current handover status and specifications directly with the developer.
                  </>
                )}
              </p>
              <p className="mt-2">Fedari Inspection is an independent inspection provider and is not affiliated with, endorsed by, or acting on behalf of {data.developerName} or any developer. We document conditions observed on the inspection date; rectification and any warranty coverage remain subject to your contract.</p>
            </div>
          </section>

          {/* Pricing */}
          <section className="wrap pb-14">
            <h2 className="text-2xl mb-4">Inspection pricing</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-[80ch]">
              {PRICE_GUIDE.map((row) => (
                <div key={row.type} className="border border-line rounded-xl p-4 bg-white">
                  <div className="text-sm text-muted mb-1">{row.type}</div>
                  <div className="font-display text-blue">{row.price}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted max-w-[64ch] mt-4">{PRICING_NOTE}</p>
            <button onClick={onQuote} className="mt-5 inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
              Get quote &amp; availability <ArrowRight size={16} />
            </button>
          </section>

          {/* FAQ */}
          <section className="wrap pb-14">
            <h2 className="text-2xl mb-5">{data.communityName} inspection — FAQs</h2>
            <div className="space-y-4 max-w-[80ch]">
              {data.faqs.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related */}
          <section className="wrap pb-16">
            <h2 className="text-xl mb-4">Related inspections &amp; communities</h2>
            <div className="flex flex-wrap gap-3">
              {data.relatedSlugs.map((href) => (
                <Link key={href} to={href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {relatedLabel(href)} <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted bg-soft border border-line rounded-full px-3 py-1.5">
      <Icon size={13} className="text-blue" aria-hidden="true" /> {children}
    </span>
  );
}
